import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import ManagementHeader from './ManagementHeader';
import MgField from './MgField';
import PermissionTree from './PermissionTree';
import { fetchUserDetailForManagement, updateUser, deleteUser, fetchRoleGroups } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import './management.css';

const DASH = '—';
const show = (v) => (v == null || v === '' || v === '-' ? DASH : v);
const STATUS_BADGE = { '활성': 'success', '비활성': 'neutral', '잠김': 'error' };

function UserDetailPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { userId } = useParams();
    const [searchParams] = useSearchParams();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [roleGroups, setRoleGroups] = useState([]);

    // 권한 그룹 목록 로드
    useEffect(() => {
        const loadRoleGroups = async () => {
            try {
                const data = await fetchRoleGroups();
                setRoleGroups(data);
            } catch (err) {
                console.error('권한 그룹 목록 조회 실패:', err);
            }
        };
        loadRoleGroups();
    }, []);

    // URL 쿼리 파라미터에서 edit 모드 확인
    useEffect(() => {
        const editMode = searchParams.get('edit');
        if (editMode === 'true') {
            setIsEditing(true);
        } else {
            setIsEditing(false);
        }
    }, [searchParams]);

    // API에서 사용자 데이터 가져오기
    useEffect(() => {
        const loadUserData = async () => {
            try {
                setLoading(true);
                const data = await fetchUserDetailForManagement(userId);

                setUserData(data);
                setFormData({
                    id: data.id,
                    name: data.name,
                    email: data.email,
                    phone: data.phone || '',
                    password: '',
                    confirmPassword: '',
                    roleGroupId: data.roleGroup?.id || null,
                    department: data.department || '',
                    position: data.position || '',
                    status: data.status,
                    lastLogin: data.lastLogin ? formatDateTime(data.lastLogin) : '-',
                    joinDate: data.createdAt ? formatDate(data.createdAt) : '-'
                });
            } catch (err) {
                console.error('사용자 정보 조회 실패:', err);
                alert(t('pages.management.userDetail.alertLoadFail'));
                navigate('/user');
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            loadUserData();
        }
    }, [userId]);

    const [formData, setFormData] = useState({
        id: userId,
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        roleGroupId: null,
        department: '',
        position: '',
        status: 'ACTIVE',
        lastLogin: '-',
        joinDate: '-'
    });

    // 날짜 포맷 함수 — 브라우저 현지 시각 기준 (toISOString 은 UTC 라 한국에서는 9시간 이르게 보였다)
    const pad2 = (n) => String(n).padStart(2, '0');
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const d = new Date(dateString);
        return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    };

    // 날짜시간 포맷 함수
    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return '-';
        const d = new Date(dateTimeString);
        return `${formatDate(dateTimeString)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    };

    // 상태 매핑 함수 (API key -> 한글 키)
    const mapStatus = (apiStatus) => {
        const statusMap = {
            'ACTIVE': '활성',
            'INACTIVE': '비활성',
            'LOCKED': '잠김'
        };
        return statusMap[apiStatus] || '비활성';
    };

    // 한글 키 -> 번역 라벨
    const getStatusLabel = (status) => {
        // 입력이 API key 또는 한글 key 모두 허용
        const koreanKey = status === 'ACTIVE' || status === 'INACTIVE' || status === 'LOCKED' ? mapStatus(status) : status;
        const map = {
            '활성': t('pages.management.userDetail.status.active'),
            '비활성': t('pages.management.userDetail.status.inactive'),
            '잠김': t('pages.management.userDetail.status.locked'),
        };
        return map[koreanKey] || koreanKey;
    };

    // 선택된 권한 그룹 정보 (편집 모드에서는 formData에서, 읽기 모드에서는 userData에서)
    const selectedRoleGroup = isEditing
        ? roleGroups.find(rg => rg.id === parseInt(formData.roleGroupId))
        : userData?.roleGroup;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEdit = () => {
        // URL에 ?edit=true 쿼리 파라미터 추가
        navigate(`/user/${userId}?edit=true`, { replace: true });
    };

    const handleCancel = () => {
        if (isEditing) {
            // URL에서 ?edit=true 제거하고 원래 데이터로 복원
            navigate(`/user/${userId}`, { replace: true });
            if (userData) {
                setFormData({
                    id: userData.id,
                    name: userData.name,
                    email: userData.email,
                    phone: userData.phone || '',
                    password: '',
                    confirmPassword: '',
                    roleGroupId: userData.roleGroup?.id || null,
                    department: userData.department || '',
                    position: userData.position || '',
                    status: userData.status,
                    lastLogin: userData.lastLogin ? formatDateTime(userData.lastLogin) : '-',
                    joinDate: userData.createdAt ? formatDate(userData.createdAt) : '-'
                });
            }
        } else {
            navigate('/user');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        // 비밀번호 확인
        if (formData.password && formData.password !== formData.confirmPassword) {
            alert(t('pages.management.userDetail.alertPasswordMismatch'));
            return;
        }

        try {
            const updateData = {
                name: formData.name,
                phone: formData.phone,
                department: formData.department,
                position: formData.position,
                roleGroupId: parseInt(formData.roleGroupId),
                status: formData.status,
            };

            // 비밀번호가 입력된 경우에만 포함
            if (formData.password) {
                updateData.password = formData.password;
            }


            await updateUser(userId, updateData);

            alert(t('pages.management.userDetail.alertUpdateSuccess'));

            // 데이터 새로고침
            const updatedData = await fetchUserDetailForManagement(userId);
            setUserData(updatedData);
            setFormData({
                id: updatedData.id,
                name: updatedData.name,
                email: updatedData.email,
                phone: updatedData.phone || '',
                password: '',
                confirmPassword: '',
                roleGroupId: updatedData.roleGroup?.id || null,
                department: updatedData.department || '',
                position: updatedData.position || '',
                status: updatedData.status,
                lastLogin: updatedData.lastLogin ? formatDateTime(updatedData.lastLogin) : '-',
                joinDate: updatedData.createdAt ? formatDate(updatedData.createdAt) : '-'
            });

            // 저장 후 읽기 모드로 전환
            navigate(`/user/${userId}`, { replace: true });
        } catch (error) {
            console.error('사용자 정보 수정 실패:', error);
            alert(t('pages.management.userDetail.alertUpdateFail'));
        }
    };

    const handleDelete = async () => {
        if (window.confirm(t('pages.management.userDetail.confirmDelete'))) {
            try {

                await deleteUser(userId);
                alert(t('pages.management.userDetail.alertDeleteSuccess'));
                navigate('/user');
            } catch (error) {
                console.error('사용자 삭제 실패:', error);
                alert(t('pages.management.userDetail.alertDeleteFail'));
            }
        }
    };

    const L = (key) => t(`pages.management.userDetail.label.${key}`);
    const P = (key) => t(`pages.management.userDetail.placeholder.${key}`);
    const statusKo = mapStatus(formData.status);
    /* 조회 모드에서는 값, 수정 모드에서는 입력칸 */
    const field = (name, { type = 'text', required = false, placeholder = true } = {}) => (isEditing ? (
        <input type={type} name={name} className="mg-input" placeholder={placeholder ? P(name) : undefined} value={formData[name]} onChange={handleInputChange} required={required} />
    ) : (
        <div className="mg-value">{show(formData[name])}</div>
    ));

    const header = (children) => (
        <ManagementHeader
            rootLabel={t('sidebar.user.title')}
            icon="manage_accounts"
            backTo="/user"
            crumbs={[
                { label: t('sidebar.user.list'), to: '/user' },
                { label: t('pages.management.userDetail.title') }
            ]}
        >
            {children}
        </ManagementHeader>
    );

    if (loading || !userData) {
        return (
            <MainLayout>
                <div className="mg-page">
                    {header(null)}
                    <div className="mg-loading">{loading ? t('pages.management.userDetail.loading') : t('pages.management.userDetail.notFound')}</div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="mg-page">
                {header(!isEditing ? (
                    <>
                        <button type="button" className="mg-btn danger" onClick={handleDelete}>
                            <span className="material-symbols-outlined">delete</span>
                            {t('pages.management.userDetail.btn.delete')}
                        </button>
                        <button type="button" className="mg-btn primary" onClick={handleEdit}>
                            <span className="material-symbols-outlined">edit</span>
                            {t('pages.management.userDetail.btn.edit')}
                        </button>
                    </>
                ) : (
                    <>
                        <button type="button" className="mg-btn secondary" onClick={handleCancel}>
                            <span className="material-symbols-outlined">close</span>
                            {t('pages.management.userDetail.btn.cancel')}
                        </button>
                        <button type="button" className="mg-btn primary" onClick={handleSave}>
                            <span className="material-symbols-outlined">check</span>
                            {t('pages.management.userDetail.btn.save')}
                        </button>
                    </>
                ))}

                {/* 프로필 카드 */}
                <div className="mg-panel mg-profile">
                    <span className="mg-avatar large">{(formData.name || '').charAt(0)}</span>
                    <div className="mg-profile-info">
                        <h2 className="mg-profile-name">{show(formData.name)}</h2>
                        <p className="mg-profile-sub">{show(formData.email)}</p>
                        <span className={`mg-badge ${STATUS_BADGE[statusKo] || 'neutral'}`}>{getStatusLabel(formData.status)}</span>
                    </div>
                    <div className="mg-stats">
                        <div className="mg-stat">
                            <span className="mg-stat-label">{L('joinDate')}</span>
                            <span className="mg-stat-value">{show(formData.joinDate)}</span>
                        </div>
                        <div className="mg-stat">
                            <span className="mg-stat-label">{L('lastLogin')}</span>
                            <span className="mg-stat-value">{show(formData.lastLogin)}</span>
                        </div>
                    </div>
                </div>

                <form className={`mg-form ${isEditing ? 'editing' : ''}`} onSubmit={handleSave}>
                    {/* 기본 정보 */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">{t('pages.management.userDetail.section.basic')}</h3>
                        <div className="mg-grid">
                            <MgField label={L('name')}>{field('name', { required: true, placeholder: false })}</MgField>
                            <MgField label={L('email')}>{field('email', { type: 'email', required: true, placeholder: false })}</MgField>
                            {isEditing && (
                                <>
                                    <MgField label={L('newPassword')}>
                                        <input type="password" name="password" className="mg-input" placeholder={P('newPassword')} value={formData.password} onChange={handleInputChange} minLength={8} autoComplete="new-password" />
                                    </MgField>
                                    <MgField label={L('confirmPassword')}>
                                        <input type="password" name="confirmPassword" className="mg-input" placeholder={P('confirmPassword')} value={formData.confirmPassword} onChange={handleInputChange} autoComplete="new-password" />
                                    </MgField>
                                </>
                            )}
                        </div>
                    </div>

                    {/* 조직 정보 */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">{t('pages.management.userDetail.section.organization')}</h3>
                        <div className="mg-grid">
                            <MgField label={L('phone')}>{field('phone', { type: 'tel' })}</MgField>
                            <MgField label={L('department')}>{field('department')}</MgField>
                            <MgField label={L('position')}>{field('position')}</MgField>
                            <MgField label={L('status')}>
                                {isEditing ? (
                                    <select name="status" className="mg-select" value={formData.status} onChange={handleInputChange} required>
                                        <option value="ACTIVE">{t('pages.management.userDetail.statusOption.active')}</option>
                                        <option value="INACTIVE">{t('pages.management.userDetail.statusOption.inactive')}</option>
                                        <option value="LOCKED">{t('pages.management.userDetail.statusOption.locked')}</option>
                                    </select>
                                ) : (
                                    <div className="mg-value">{getStatusLabel(formData.status)}</div>
                                )}
                            </MgField>
                            <MgField label={L('roleGroup')}>
                                {isEditing ? (
                                    <select name="roleGroupId" className="mg-select" value={formData.roleGroupId ?? ''} onChange={handleInputChange} required>
                                        {roleGroups.map(rg => (
                                            <option key={rg.id} value={rg.id}>{rg.name} ({rg.code})</option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className="mg-value">
                                        {show(selectedRoleGroup?.name)}
                                        {selectedRoleGroup?.code && <span className="mg-muted">&nbsp;({selectedRoleGroup.code})</span>}
                                    </div>
                                )}
                            </MgField>
                        </div>
                    </div>

                    {/* 권한 (권한 그룹에 따라 정해짐, 읽기 전용) */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">
                            {t('pages.management.userDetail.section.permissions')}
                            <span className="mg-panel-note">{t('pages.management.userDetail.permissionsNote')}</span>
                        </h3>
                        <PermissionTree
                            permissions={selectedRoleGroup?.permissions}
                            etcLabel={t('pages.management.userDetail.permissionCategoryEtc')}
                            emptyLabel={t('pages.management.userDetail.permissionsEmpty')}
                        />
                    </div>

                    {isEditing && (
                        <div className="mg-form-actions">
                            <button type="button" className="mg-btn secondary" onClick={handleCancel}>
                                {t('pages.management.userDetail.btn.cancel')}
                            </button>
                            <button type="submit" className="mg-btn primary">
                                {t('pages.management.userDetail.btn.save')}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </MainLayout>
    );
}

export default UserDetailPage;
